import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography } from 'kangarootime-ui';
import { DayPickerRangeController } from 'react-dates';
import moment from 'moment';
import './TimeOffRequestDialog.css';

const DatePickerInput = ({
  startDatetime,
  endDatetime,
  allowRangeSelect,
  onDateSelect,
  disableToday,
  disableWeekends
}) => {
  const [focusedInput, setFocusedInput] = useState('startDate');
  const [selectedDates, setSelectedDates] = useState({ startDate: startDatetime, endDate: endDatetime });

  // date is a moment object
  const setHoursForDate = (date, hours) => moment(date).clone().startOf('day').hours(hours).minutes(0).second(0).millisecond(0);

  // whenever the time off request type is changed, reset dates
  useEffect(() => {
    if (!allowRangeSelect) {
      if (selectedDates.startDate !== null || selectedDates.endDate !== null) {
        setSelectedDates({ startDate: null, endDate: null });
      }
    }
  }, [allowRangeSelect]);

  const isOutsideRange = useCallback(day => {
    const isWeekend = day.day() === 0 || day.day() === 6;
    const hasWeekendsDisabled = disableWeekends && isWeekend;
    const hasTodayDisabled = disableToday ? moment(day).isSameOrBefore(moment(), 'date') : moment(day).isBefore(moment(), 'date');

    return hasTodayDisabled || hasWeekendsDisabled;
  }, [disableWeekends, disableToday]);

  const onDatesChange = useCallback(dates => {
    const { startDate, endDate } = dates;

    // before bubbling dates up, set the timeframe to 9am-5pm
    if (allowRangeSelect) {
      setSelectedDates({ startDate, endDate });
      onDateSelect({
        startDate: setHoursForDate(startDate, 9),
        endDate: setHoursForDate(endDate, 17)
      });
    } else {
      setSelectedDates({ startDate, endDate: startDate });
      setFocusedInput('startDate');
      onDateSelect({
        startDate: setHoursForDate(startDate, 9),
        endDate: setHoursForDate(startDate, 17)
      });
    }
  }, [onDateSelect, allowRangeSelect]);

  const onFocusChange = useCallback(focusedInput => {
    // from react-date's example component: force the focusedInput to always be truthy so that dates are always selectable
    setFocusedInput(!focusedInput ? 'startDate' : focusedInput);
  }, []);

  const selectingRange = !moment(selectedDates.endDate).isSame(moment(selectedDates.startDate), 'day');
  const title = selectedDates.startDate && selectedDates.endDate && selectingRange ? `${moment(selectedDates.startDate).format('MMM D, YYYY')} - ${moment(selectedDates.endDate).format('MMM D, YYYY')}` : moment(selectedDates.startDate).format('MMM D, YYYY');

  return (
    <Fragment>
      { selectedDates.startDate && <Typography type='meta'>Requested: {title}</Typography> }
      <DayPickerRangeController
        hideKeyboardShortcutsPanel
        numberOfMonths={1}
        minimumNights={0}
        focusedInput={focusedInput}
        startDate={selectedDates.startDate}
        endDate={selectedDates.endDate}
        onFocusChange={onFocusChange}
        onDatesChange={onDatesChange}
        isOutsideRange={isOutsideRange}
      />
    </Fragment>
  );
};

DatePickerInput.propTypes = {
  startDatetime: PropTypes.object,
  endDatetime: PropTypes.object,
  onDateSelect: PropTypes.func,
  allowRangeSelect: PropTypes.bool,
  disableToday: PropTypes.bool,
  disableWeekends: PropTypes.bool
};

DatePickerInput.defaultProps = {
  startDatetime: null,
  endDatetime: null,
  disableToday: true,
  disableWeekends: true
};

export default DatePickerInput;
